<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="880"
    scrollable
    content-class="parchment-edit-dialog"
    @click:outside="close"
  >
    <v-card v-if="dialogReady">
      <v-toolbar color="secondary" class="white--text" flat height="48">
        <v-toolbar-title class="font-weight-bold">
          {{ dialogTitle }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="parchment-edit-dialog__body pt-6 pb-2 px-6">
        <v-form ref="form" lazy-validation>
          <v-row class="parchment-edit-dialog__top-layout">
            <v-col cols="12" md="6" class="pr-md-3">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.deliveryDate") }}
                </label>
                <v-menu
                  v-model="deliveryDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="deliveryDateDisplay"
                      class="parchment-edit-dialog__input"
                      outlined
                      dense
                      hide-details
                      readonly
                      :placeholder="deliveryDatePlaceholder"
                      v-bind="attrs"
                      v-on="on"
                      append-icon="mdi-calendar"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.deliveryDate"
                    no-title
                    scrollable
                    @input="onDeliveryDatePicked"
                  ></v-date-picker>
                </v-menu>
              </div>

              <div class="parchment-edit-dialog__field mt-5">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.greenCoffee.selectProcessingBatch") }}
                </label>
                <v-menu
                  v-model="batchPickerMenuOpen"
                  offset-y
                  :close-on-content-click="false"
                  :nudge-bottom="4"
                  max-width="100%"
                  content-class="parchment-batch-picker-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="parchment-batch-activator"
                      :class="{ 'parchment-batch-activator--kenya': isKenyaClient }"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <div class="parchment-batch-activator__chips">
                        <v-chip
                          v-for="batch in selectedBatches"
                          :key="batch.id"
                          small
                          close
                          class="parchment-batch-activator__chip"
                          :color="isKenyaClient ? '#a75300' : 'primary'"
                          text-color="white"
                          @click.stop
                          @click:close="removeProcessingBatch(batch.id)"
                        >
                          {{ batch.batchId }}
                        </v-chip>
                        <span
                          v-if="!selectedBatches.length"
                          class="parchment-batch-activator__placeholder"
                        >
                          {{ processingBatchPlaceholder }}
                        </span>
                      </div>
                      <v-icon class="parchment-batch-activator__icon">mdi-chevron-down</v-icon>
                    </div>
                  </template>

                  <v-card class="parchment-batch-picker" elevation="4">
                    <v-card-title class="parchment-batch-picker__title py-3 px-4">
                      {{ $t("memberDataSection.parchmentDelivery.bulkImport.selectProcessingBatchesTitle") }}
                    </v-card-title>

                    <div class="px-4 pb-3">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <label class="parchment-edit-dialog__label">
                            {{ $t("memberDataSection.parchmentDelivery.bulkImport.searchLabel") }}
                          </label>
                          <v-text-field
                            v-model="batchSearchQuery"
                            class="parchment-edit-dialog__input"
                            dense
                            outlined
                            hide-details
                            :placeholder="$t('memberDataSection.parchmentDelivery.bulkImport.searchByBatchId')"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <label class="parchment-edit-dialog__label">
                            {{ $t("memberDataSection.parchmentDelivery.bulkImport.coffeeType") }}
                          </label>
                          <v-select
                            v-model="batchCoffeeTypeFilter"
                            class="parchment-edit-dialog__input"
                            :items="batchCoffeeTypeFilterOptions"
                            dense
                            outlined
                            hide-details
                          ></v-select>
                        </v-col>
                      </v-row>
                    </div>

                    <div class="parchment-batch-picker__toolbar px-4 pb-2">
                      <v-checkbox
                        :input-value="allFilteredBatchesSelected"
                        :indeterminate="someFilteredBatchesSelected"
                        hide-details
                        class="parchment-batch-picker__all-checkbox mt-0 pt-0"
                        :color="batchCheckboxColor"
                        @click.stop="onToggleAllFilteredBatches(!allFilteredBatchesSelected)"
                      >
                        <template v-slot:label>
                          <span class="parchment-batch-picker__all-label">
                            {{ $t("memberDataSection.parchmentDelivery.bulkImport.selectAllBatches") }}
                          </span>
                        </template>
                      </v-checkbox>
                      <span class="parchment-batch-picker__summary">
                        {{ batchSelectionSummaryText }}
                      </span>
                    </div>

                    <div class="parchment-batch-picker__table-head px-4">
                      <div class="parchment-batch-picker__col parchment-batch-picker__col--check"></div>
                      <div class="parchment-batch-picker__col parchment-batch-picker__col--lot">
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.lotIdAndDate") }}
                      </div>
                      <div class="parchment-batch-picker__col parchment-batch-picker__col--variety">
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.coffeeVarietyAndType") }}
                      </div>
                      <div class="parchment-batch-picker__col parchment-batch-picker__col--qty text-right">
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.batchQuantity") }}
                      </div>
                    </div>

                    <div class="parchment-batch-picker__list">
                      <div v-if="batchesLoading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                      </div>
                      <template v-else>
                      <button
                        v-for="batch in filteredProcessingBatches"
                        :key="batch.id"
                        type="button"
                        class="parchment-batch-picker__row"
                        @click="toggleProcessingBatch(batch.id)"
                      >
                        <div class="parchment-batch-picker__col parchment-batch-picker__col--check">
                          <v-checkbox
                            :input-value="isProcessingBatchSelected(batch.id)"
                            hide-details
                            class="mt-0 pt-0"
                            :color="batchCheckboxColor"
                            readonly
                          />
                        </div>
                        <div class="parchment-batch-picker__col parchment-batch-picker__col--lot">
                          <div class="parchment-batch-picker__line">{{ batch.batchId }}</div>
                          <div class="parchment-batch-picker__line parchment-batch-picker__line--sub">
                            {{ batch.lotDate }}
                          </div>
                        </div>
                        <div class="parchment-batch-picker__col parchment-batch-picker__col--variety">
                          <div class="parchment-batch-picker__line">{{ batch.variety }}</div>
                          <div class="parchment-batch-picker__line parchment-batch-picker__line--sub">
                            {{
                              $t("memberDataSection.parchmentDelivery.bulkImport.batchCoffeeTypeLine", {
                                type: batch.coffeeType,
                              })
                            }}
                          </div>
                        </div>
                        <div class="parchment-batch-picker__col parchment-batch-picker__col--qty text-right">
                          <div class="parchment-batch-picker__line">{{ batch.quantity }} kg</div>
                        </div>
                      </button>

                      <p
                        v-if="!filteredProcessingBatches.length"
                        class="parchment-batch-picker__empty px-4 py-6 mb-0 text-center"
                      >
                        {{ $t("memberDataSection.parchmentDelivery.noDataFound") }}
                      </p>
                      </template>
                    </div>
                  </v-card>
                </v-menu>
              </div>

              <div
                v-if="selectedBatches.length"
                class="parchment-edit-dialog__batch-table-wrap mt-4"
              >
                <table class="parchment-edit-dialog__batch-table">
                  <thead>
                    <tr>
                      <th>
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.lotIdAndDate") }}
                      </th>
                      <th>
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.typeAndProcess") }}
                      </th>
                      <th>
                        {{ $t("memberDataSection.parchmentDelivery.bulkImport.batchQuantity") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="batch in selectedBatches" :key="batch.id">
                      <td>
                        <div class="parchment-edit-dialog__batch-line">{{ batch.batchId }}</div>
                        <div class="parchment-edit-dialog__batch-line">{{ batch.lotDate }}</div>
                      </td>
                      <td>
                        <div class="parchment-edit-dialog__batch-line">{{ batch.variety }}</div>
                        <div class="parchment-edit-dialog__batch-line">
                          {{
                            $t("memberDataSection.parchmentDelivery.bulkImport.batchCoffeeTypeLine", {
                              type: batch.coffeeType,
                            })
                          }}
                        </div>
                      </td>
                      <td>
                        <div class="parchment-edit-dialog__batch-line">{{ batch.quantity }} kg</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-col>

            <v-col cols="12" md="6" class="d-none d-md-block pl-md-3"></v-col>
          </v-row>

          <v-divider class="parchment-edit-dialog__section-divider my-6"></v-divider>

          <v-row class="parchment-edit-dialog__form-grid" dense>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.greenCoffee.totalQuantity") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">kg</span>
                </div>
                <v-text-field
                  v-if="form.processingBatchIds.length"
                  :value="totalQuantity"
                  outlined
                  dense
                  hide-details
                  readonly
                  class="parchment-edit-dialog__readonly-field parchment-edit-dialog__input"
                ></v-text-field>
                <v-text-field
                  v-else
                  v-model.number="form.manualTotalQuantity"
                  type="number"
                  outlined
                  dense
                  hide-details
                  class="parchment-edit-dialog__input"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.outturnNumber") }}
                </label>
                <v-text-field
                  v-model="form.outturnNumber"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.season") }}
                </label>
                <v-select
                  v-model="form.season"
                  class="parchment-edit-dialog__input"
                  :items="seasonOptions"
                  :placeholder="selectionPlaceholder"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.growerNameFactory") }}
                </label>
                <v-text-field
                  v-model="form.growerName"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.growerCodeFactory") }}
                </label>
                <v-text-field
                  v-model="form.growerCode"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <label class="parchment-edit-dialog__label">
                  {{ $t("memberDataSection.parchmentDelivery.bulkImport.coffeeType") }}
                </label>
                <v-select
                  v-model="form.coffeeType"
                  class="parchment-edit-dialog__input"
                  :items="coffeeTypeOptions"
                  :placeholder="selectionPlaceholder"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.grossWeight") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">kg</span>
                </div>
                <v-text-field
                  v-model.number="form.grossWeight"
                  type="number"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.greenCoffee.unitSize") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">kg</span>
                </div>
                <v-text-field
                  v-model.number="form.unitSize"
                  type="number"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>
          </v-row>

          <div class="parchment-edit-dialog__bags-summary">
            <p class="parchment-edit-dialog__bags-summary-line">
              <span class="font-weight-bold">
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.totalBagsLabel") }}:
              </span>
              {{ bagsSummaryBags }}
            </p>
            <p class="parchment-edit-dialog__bags-summary-line">
              <span class="font-weight-bold">
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.leftoverLabel") }}:
              </span>
              {{ bagsSummaryLeftover }}
            </p>
          </div>

          <section v-if="showLeftoverHandlingOptions" class="parchment-edit-dialog__leftover-section">
            <p class="parchment-edit-dialog__leftover-prompt">
              {{
                $t("memberDataSection.parchmentDelivery.bulkImport.leftoverHandlingPrompt", {
                  totalWeight: totalQuantity,
                  bags: totalBags,
                  leftover: leftoverKg,
                })
              }}
            </p>

            <div
              class="parchment-edit-dialog__leftover-options"
              :class="{ 'parchment-edit-dialog__leftover-options--kenya': isKenyaClient }"
            >
              <label class="parchment-edit-dialog__leftover-option">
                <input
                  v-model="form.leftoverHandling"
                  type="radio"
                  name="leftover-handling"
                  value="attach"
                />
                <div class="parchment-edit-dialog__leftover-option-text">
                  <p class="parchment-edit-dialog__leftover-title">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.attachLeftoverTitle") }}
                  </p>
                  <p class="parchment-edit-dialog__leftover-desc">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.attachLeftoverDescription") }}
                  </p>
                </div>
              </label>

              <label class="parchment-edit-dialog__leftover-option">
                <input
                  v-model="form.leftoverHandling"
                  type="radio"
                  name="leftover-handling"
                  value="management"
                />
                <div class="parchment-edit-dialog__leftover-option-text">
                  <p class="parchment-edit-dialog__leftover-title">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.moveToLeftoverTitle") }}
                  </p>
                  <p class="parchment-edit-dialog__leftover-desc">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.moveToLeftoverDescription") }}
                  </p>
                </div>
              </label>
            </div>
          </section>

          <v-row class="parchment-edit-dialog__form-grid" dense>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.netWeightWithBags") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">kg</span>
                </div>
                <v-text-field
                  v-model.number="form.netWeightWithBags"
                  type="number"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                  @input="syncNetWeight"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.netWeight") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">kg</span>
                </div>
                <v-text-field
                  :value="form.netWeight"
                  type="number"
                  class="parchment-edit-dialog__readonly-field parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                  readonly
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="parchment-edit-dialog__field">
                <div class="parchment-edit-dialog__label-row">
                  <label class="parchment-edit-dialog__label mb-0">
                    {{ $t("memberDataSection.parchmentDelivery.bulkImport.moistureLabel") }}
                  </label>
                  <span class="parchment-edit-dialog__unit">%</span>
                </div>
                <v-text-field
                  v-model.number="form.moisture"
                  type="number"
                  class="parchment-edit-dialog__input"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="d-none d-md-block"></v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="parchment-edit-dialog__footer px-6 pb-5 pt-0">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          height="40"
          min-width="128"
          class="parchment-edit-dialog__footer-btn text-uppercase font-weight-bold mr-3"
          :class="isKenyaClient ? 'parchment-edit-dialog__cancel-btn--kenya' : ''"
          :color="isKenyaClient ? undefined : 'primary'"
          @click="close"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          height="40"
          min-width="128"
          class="parchment-edit-dialog__footer-btn text-uppercase font-weight-bold white--text"
          :class="isKenyaClient ? 'parchment-edit-dialog__submit-btn--kenya' : ''"
          :color="isKenyaClient ? undefined : 'primary'"
          :loading="submitting"
          :disabled="submitting"
          @click="submit"
        >
          {{ $t("submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { isKenyaClient } from "@/utils";
import {
  computeParchmentBagsFromQuantity,
  formatParchmentDisplayDate,
  normalizeParchmentDeliveryDateInput,
} from "@/utils/parchmentDeliveryBulkImport";
import ParchmentDeliveryService from "@/_services/ParchmentDeliveryService";

export default {
  name: "ParchmentDeliveryAddDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    mode: {
      type: String,
      default: "add",
      validator: (v) => ["add", "edit"].includes(v),
    },
    buyingStationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      visible: this.value,
      submitting: false,
      deliveryDateMenu: false,
      deliveryDateDisplay: "",
      processingBatches: [],
      batchesLoading: false,
      seasonOptions: ["2025 - 2026", "2024 - 2025", "2023 - 2024"],
      batchPickerMenuOpen: false,
      batchSearchQuery: "",
      batchCoffeeTypeFilter: "",
      form: this.buildEmptyForm(),
    };
  },
  computed: {
    isAddMode() {
      return this.mode === "add";
    },
    dialogReady() {
      return this.isAddMode || Boolean(this.record);
    },
    dialogTitle() {
      if (this.isAddMode) {
        return this.$t("memberDataSection.parchmentDelivery.addParchmentDelivery");
      }
      return this.$t("memberDataSection.parchmentDelivery.bulkImport.editParchmentDeliveryTitle");
    },
    selectionPlaceholder() {
      return this.$t("memberDataSection.pOrder.makeSelection");
    },
    deliveryDatePlaceholder() {
      if (this.deliveryDateDisplay) return "";
      return this.$t("memberDataSection.parchmentDelivery.selectDeliveryDate");
    },
    showLeftoverHandlingOptions() {
      return this.leftoverKg > 0;
    },
    bagsSummaryBags() {
      if (this.totalQuantity > 0 && Number(this.form.unitSize) > 0) {
        return this.totalBags;
      }
      return "—";
    },
    bagsSummaryLeftover() {
      if (this.totalQuantity > 0 && Number(this.form.unitSize) > 0) {
        return `${this.leftoverKg} kg`;
      }
      return "—";
    },
    isKenyaClient() {
      return isKenyaClient();
    },
    coffeeTypeOptions() {
      return this.isKenyaClient
        ? ["P1", "P2", "P3", "P-light","PL", "AA", "AB"]
        : ["P1", "PL", "AA", "AB"];
    },
    batchCheckboxColor() {
      return this.isKenyaClient ? "#a75300" : "primary";
    },
    batchCoffeeTypeFilterOptions() {
      const base = "memberDataSection.parchmentDelivery.bulkImport";
      const types = [
        ...new Set(this.processingBatches.map((b) => b.coffeeType).filter(Boolean)),
      ];
      return [
        { text: this.$t(`${base}.filterAllCoffeeTypes`), value: "" },
        ...types.map((type) => ({ text: type, value: type })),
      ];
    },
    filteredProcessingBatches() {
      let list = this.processingBatches;
      const query = (this.batchSearchQuery || "").trim().toLowerCase();
      if (query) {
        list = list.filter((b) => String(b.batchId || "").toLowerCase().includes(query));
      }
      if (this.batchCoffeeTypeFilter) {
        list = list.filter((b) => b.coffeeType === this.batchCoffeeTypeFilter);
      }
      return list;
    },
    allFilteredBatchesSelected() {
      const filtered = this.filteredProcessingBatches;
      if (!filtered.length) return false;
      const selected = new Set(this.form.processingBatchIds || []);
      return filtered.every((b) => selected.has(b.id));
    },
    someFilteredBatchesSelected() {
      const filtered = this.filteredProcessingBatches;
      if (!filtered.length) return false;
      const selected = new Set(this.form.processingBatchIds || []);
      const count = filtered.filter((b) => selected.has(b.id)).length;
      return count > 0 && count < filtered.length;
    },
    batchSelectionSummaryText() {
      const base = "memberDataSection.parchmentDelivery.bulkImport";
      return this.$t(`${base}.batchesSelectedSummary`, {
        count: this.selectedBatches.length,
        kg: this.totalQuantity,
      });
    },
    selectedBatches() {
      const ids = new Set(this.form.processingBatchIds || []);
      return this.processingBatches.filter((b) => ids.has(b.id));
    },
    totalQuantity() {
      if (!this.form.processingBatchIds.length) {
        return this.form.manualTotalQuantity != null ? this.form.manualTotalQuantity : 0;
      }
      return this.selectedBatches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0);
    },
    totalBags() {
      return computeParchmentBagsFromQuantity(this.totalQuantity, this.form.unitSize).totalBags;
    },
    leftoverKg() {
      return computeParchmentBagsFromQuantity(this.totalQuantity, this.form.unitSize).leftoverKg;
    },
    processingBatchPlaceholder() {
      if (this.selectedBatches.length) return "";
      return this.$t("memberDataSection.pOrder.makeSelection");
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        if (this.isAddMode) {
          this.resetForAdd();
        } else {
          this.initFromRecord(this.record);
        }
        this.fetchProcessingBatches();
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
    record: {
      immediate: true,
      handler(rec) {
        if (this.visible && rec) {
          this.initFromRecord(rec);
        }
      },
    },
    "form.deliveryDate"(iso) {
      this.deliveryDateDisplay = iso ? formatParchmentDisplayDate(iso) : "";
    },
    totalBags(bags) {
      if (bags != null && !Number.isNaN(bags)) {
        this.form.bags = bags;
      }
    },
  },
  methods: {
    async fetchProcessingBatches() {
      if (!this.buyingStationId) return;
      this.batchesLoading = true;
      try {
        const res = await ParchmentDeliveryService.getProcessingBatchesForBuyingStation(
          this.buyingStationId
        );
        const rows = res?.data?.rows || [];
        this.processingBatches = rows.map((b) => ({
          id: b.id,
          batchId: b.batchCode || `PB-${String(b.id).padStart(5, "0")}`,
          lotDate: b.startDate || b.endDate || "",
          variety: b.processingType?.name || "",
          coffeeType:
            (Array.isArray(b.buyingStationOrder) && b.buyingStationOrder[0]?.coffeeType) || "",
          quantity: b.parchmentOut != null ? b.parchmentOut : 0,
        })).filter((b) => b.quantity > 0);
      } catch (_) {
        this.processingBatches = [];
      } finally {
        this.batchesLoading = false;
      }
    },
    resetForAdd() {
      this.form = this.buildEmptyForm();
      this.deliveryDateDisplay = "";
      this.batchSearchQuery = "";
      this.batchCoffeeTypeFilter = "";
      this.batchPickerMenuOpen = false;
      this.deliveryDateMenu = false;
    },
    buildEmptyForm() {
      return {
        deliveryDate: "",
        processingBatchIds: [],
        outturnNumber: "",
        season: "",
        growerCode: "",
        growerName: "",
        coffeeType: "",
        unitSize: 60,
        grossWeight: null,
        netWeightWithBags: null,
        netWeight: null,
        moisture: null,
        bags: null,
        leftoverHandling: "management",
        manualTotalQuantity: null,
      };
    },
    initFromRecord(record) {
      const p = record?.payloadJsonData || record?.payload || {};
      const batchIds = Array.isArray(p.processingBatchIds) ? p.processingBatchIds : [];

      this.form = {
        deliveryDate: normalizeParchmentDeliveryDateInput(p.deliveryDate) || "",
        processingBatchIds: batchIds,
        outturnNumber: p.outturnNumber != null ? String(p.outturnNumber) : "",
        season: p.season || "2025 - 2026",
        growerCode: p.grower != null ? String(p.grower) : p.growerCode != null ? String(p.growerCode) : "",
        growerName: p.growerName != null ? String(p.growerName) : "",
        coffeeType: p.coffeeType != null ? String(p.coffeeType) : "",
        unitSize: p.unitSize != null ? Number(p.unitSize) : 60,
        grossWeight: p.grossWeight != null && p.grossWeight !== "" ? Number(p.grossWeight) : null,
        netWeightWithBags:
          p.netWeightWithBags != null && p.netWeightWithBags !== ""
            ? Number(p.netWeightWithBags)
            : null,
        netWeight:
          p.netWeight != null && p.netWeight !== ""
            ? Number(p.netWeight)
            : p.netWeightWithBags != null && p.netWeightWithBags !== ""
              ? Number(p.netWeightWithBags)
              : null,
        moisture: p.moisture != null && p.moisture !== "" ? Number(p.moisture) : null,
        bags: p.bags != null && p.bags !== "" ? Number(p.bags) : null,
        leftoverHandling: p.leftoverHandling === "attach" ? "attach" : "management",
        manualTotalQuantity: p.totalQuantity != null && p.totalQuantity !== "" ? Number(p.totalQuantity) : null,
      };
      this.deliveryDateDisplay = this.form.deliveryDate
        ? formatParchmentDisplayDate(this.form.deliveryDate)
        : "";
      this.batchSearchQuery = "";
      this.batchCoffeeTypeFilter = "";
    },
    isProcessingBatchSelected(batchId) {
      return (this.form.processingBatchIds || []).includes(batchId);
    },
    toggleProcessingBatch(batchId) {
      const ids = [...(this.form.processingBatchIds || [])];
      const idx = ids.indexOf(batchId);
      if (idx >= 0) {
        ids.splice(idx, 1);
      } else {
        ids.push(batchId);
      }
      this.form.processingBatchIds = ids;
    },
    onToggleAllFilteredBatches(selected) {
      if (selected) {
        const current = new Set(this.form.processingBatchIds || []);
        this.filteredProcessingBatches.forEach((b) => current.add(b.id));
        this.form.processingBatchIds = Array.from(current);
        return;
      }
      const filteredIdSet = new Set(this.filteredProcessingBatches.map((b) => b.id));
      const remaining = (this.form.processingBatchIds || []).filter((id) => !filteredIdSet.has(id));
      this.form.processingBatchIds = remaining.length ? [] : remaining;
    },
    removeProcessingBatch(batchId) {
      this.form.processingBatchIds = (this.form.processingBatchIds || []).filter(
        (id) => id !== batchId
      );
    },
    onDeliveryDatePicked() {
      this.deliveryDateMenu = false;
      this.deliveryDateDisplay = this.form.deliveryDate
        ? formatParchmentDisplayDate(this.form.deliveryDate)
        : "";
    },
    syncNetWeight() {
      const nwb = this.form.netWeightWithBags;
      this.form.netWeight = nwb != null && nwb !== "" ? Number(nwb) : null;
    },
    buildPayload() {
      const payload = {
        deliveryDate: this.form.deliveryDate,
        processingBatchIds: [...(this.form.processingBatchIds || [])],
        outturnNumber: String(this.form.outturnNumber || "").trim(),
        season: this.form.season,
        grower: String(this.form.growerCode || "").trim(),
        growerCode: String(this.form.growerCode || "").trim(),
        growerName: String(this.form.growerName || "").trim(),
        coffeeType: this.form.coffeeType,
        unitSize: this.form.unitSize,
        grossWeight: this.form.grossWeight,
        netWeightWithBags: this.form.netWeightWithBags,
        netWeight: this.form.netWeight,
        moisture: this.form.moisture,
        bags: this.totalBags,
        leftoverHandling: this.form.leftoverHandling,
        totalQuantity: this.totalQuantity,
      };
      if (!this.isAddMode) {
        payload.id =
          this.record?.payloadJsonData?.id ||
          this.record?.payload?.id ||
          this.record?.id ||
          "";
      }
      if (this.buyingStationId != null && this.buyingStationId !== "") {
        payload.buyingStationId = this.buyingStationId;
      }
      return payload;
    },
    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const payload = this.buildPayload();
        if (this.isAddMode) {
          await ParchmentDeliveryService.createParchmentDelivery(payload);
          this.$notify({
            type: "success",
            title: this.$t("success"),
            text: this.$t("memberDataSection.parchmentDelivery.addParchmentDeliverySaved"),
          });
        } else {
          const id =
            this.record?.payloadJsonData?.id ||
            this.record?.payload?.id ||
            this.record?.id;
          await ParchmentDeliveryService.updateParchmentDelivery(id, payload);
          this.$notify({
            type: "success",
            title: this.$t("success"),
            text: this.$t("memberDataSection.parchmentDelivery.bulkImport.editRecordSaved"),
          });
        }
        this.visible = false;
        this.$emit("input", false);
        this.$emit("saved");
        this.$emit("close");
      } catch (err) {
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      } finally {
        this.submitting = false;
      }
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped lang="scss">

.parchment-edit-dialog__body {
  font-family: "Poppins", sans-serif;
}

.parchment-edit-dialog__top-layout {
  margin-bottom: 0;
}

.parchment-edit-dialog__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #616161;
  margin-bottom: 8px;
}

.parchment-edit-dialog__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.parchment-edit-dialog__label-row .parchment-edit-dialog__label {
  margin-bottom: 0;
}

.parchment-edit-dialog__unit {
  font-size: 13px;
  font-weight: 500;
  color: #616161;
}

.parchment-edit-dialog__field {
  width: 100%;
}

.parchment-edit-dialog__form-grid > .col {
  padding-top: 10px;
  padding-bottom: 10px;
}

.parchment-edit-dialog__leftover-section {
  margin-top: 20px;
  margin-bottom: 28px;
}

.parchment-edit-dialog__bags-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 20px 0 20px;
}

.parchment-edit-dialog__bags-summary-line {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #212121;
}

.parchment-edit-dialog__leftover-prompt {
  margin: 20px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: #212121;
}

.parchment-edit-dialog__leftover-option-text {
  flex: 1;
  min-width: 0;
}

.parchment-edit-dialog__footer {
  border-top: none;
}

.parchment-edit-dialog__input ::v-deep fieldset {
  border-color: #e0e0e0 !important;
}

.parchment-edit-dialog__input ::v-deep .v-input__slot {
  min-height: 40px !important;
  font-size: 14px;
}

.parchment-edit-dialog__input ::v-deep .v-icon {
  color: #9e9e9e !important;
}

.parchment-edit-dialog__section-divider {
  border-color: #eeeeee;
}

.parchment-edit-dialog__readonly-field ::v-deep .v-input__slot {
  background-color: #f5f5f5 !important;
}

.parchment-batch-activator {
  display: flex;
  align-items: flex-start;
  min-height: 40px;
  padding: 6px 36px 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  background: #fff;
}

.parchment-batch-activator--kenya:focus-within {
  border-color: #a75300;
}

.parchment-batch-activator__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.parchment-batch-activator__chip {
  margin: 0 !important;
  height: 24px !important;
  font-size: 12px !important;
}

.parchment-batch-activator__chip ::v-deep .v-chip__close {
  font-size: 16px !important;
}

.parchment-batch-activator__placeholder {
  font-size: 14px;
  color: #9e9e9e;
  line-height: 32px;
}

.parchment-batch-activator__icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #9e9e9e !important;
  font-size: 20px !important;
}

.parchment-batch-picker {
  width: 100%;
  max-width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.parchment-batch-picker__title {
  font-size: 15px;
  font-weight: 600;
  color: #212121;
  line-height: 1.35;
}

.parchment-batch-picker__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.parchment-batch-picker__all-label {
  font-size: 14px;
  font-weight: 500;
  color: #212121;
}

.parchment-batch-picker__summary {
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  white-space: nowrap;
}

.parchment-batch-picker__table-head,
.parchment-batch-picker__row {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.parchment-batch-picker__table-head {
  padding-bottom: 8px;
  border-bottom: 1px solid #eeeeee;
  font-size: 13px;
  font-weight: 700;
  color: #424242;
}

.parchment-batch-picker__list {
  max-height: 280px;
  overflow-y: auto;
}

.parchment-batch-picker__row {
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.parchment-batch-picker__row:hover {
  background-color: #fafafa;
}

.parchment-batch-picker__row:last-child {
  border-bottom: none;
}

.parchment-batch-picker__col {
  flex-shrink: 0;
  padding-right: 8px;
}

.parchment-batch-picker__col--check {
  width: 36px;
  padding-right: 4px;
}

.parchment-batch-picker__col--lot {
  width: 28%;
}

.parchment-batch-picker__col--variety {
  flex: 1;
  min-width: 0;
}

.parchment-batch-picker__col--qty {
  width: 72px;
  padding-right: 0;
}

.parchment-batch-picker__line {
  font-size: 14px;
  line-height: 1.45;
  color: #212121;
}

.parchment-batch-picker__line--sub {
  font-size: 13px;
  color: #616161;
}

.parchment-batch-picker__empty {
  font-size: 14px;
  color: #757575;
}

.parchment-edit-dialog__batch-table-wrap {
  border-top: 1px solid #eeeeee;
}

.parchment-edit-dialog__batch-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.parchment-edit-dialog__batch-table thead th {
  padding: 14px 12px 10px 0;
  font-size: 13px;
  font-weight: 700;
  color: #424242;
  text-align: left;
  vertical-align: bottom;
  background: #fff;
  border-bottom: 1px solid #eeeeee;
}

.parchment-edit-dialog__batch-table thead th:nth-child(1) {
  width: 32%;
}

.parchment-edit-dialog__batch-table thead th:nth-child(2) {
  width: 44%;
}

.parchment-edit-dialog__batch-table thead th:nth-child(3) {
  width: 24%;
  padding-right: 0;
}

.parchment-edit-dialog__batch-table tbody td {
  padding: 14px 12px 14px 0;
  vertical-align: top;
  border-bottom: 1px solid #eeeeee;
}

.parchment-edit-dialog__batch-table tbody tr:last-child td {
  border-bottom: none;
}

.parchment-edit-dialog__batch-line {
  font-size: 14px;
  line-height: 1.45;
  color: #212121;
}

.parchment-edit-dialog__batch-line + .parchment-edit-dialog__batch-line {
  margin-top: 2px;
}

.parchment-edit-dialog__leftover-options {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.parchment-edit-dialog__leftover-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  cursor: pointer;
}

.parchment-edit-dialog__leftover-option input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #00bd73;
  border-radius: 50%;
  background: #fff;
}

.parchment-edit-dialog__leftover-option input:checked {
  border-color: #00bd73;
  background-image: radial-gradient(circle, #00bd73 0 42%, transparent 48%);
}

.parchment-edit-dialog__leftover-options--kenya .parchment-edit-dialog__leftover-option input {
  border-color: #a75300;
}

.parchment-edit-dialog__leftover-options--kenya .parchment-edit-dialog__leftover-option input:checked {
  border-color: #a75300;
  background-image: radial-gradient(circle, #a75300 0 42%, transparent 48%);
}

.parchment-edit-dialog__leftover-title {
  margin: 0 0 6px;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.4;
  color: #212121;
}

.parchment-edit-dialog__leftover-desc {
  margin: 0;
  font-size: 13px;
  color: #616161;
  line-height: 1.5;
}

.parchment-edit-dialog__cancel-btn--kenya {
  border-color: #a75300 !important;
  color: #a75300 !important;
}

.parchment-edit-dialog__submit-btn--kenya {
  background-color: #a75300 !important;
}
</style>
